import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

export default function CookieConsent({ onOpenCookiePolicy, onOpenPrivacyPolicy }) {
    const { language } = useLanguage();
    const [isVisible, setIsVisible] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, cannot be changed
        analytics: false,
        marketing: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookie_consent');
        if (!consent) {
            // Show banner after a short delay
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        const consent = {
            necessary: true,
            analytics: true,
            marketing: true,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookie_consent', JSON.stringify(consent));
        setIsVisible(false);

        // Initialize analytics if accepted
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'granted',
                'ad_storage': 'granted'
            });
        }
    };

    const handleRejectAll = () => {
        const consent = {
            necessary: true,
            analytics: false,
            marketing: false,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookie_consent', JSON.stringify(consent));
        setIsVisible(false);

        // Deny analytics
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': 'denied',
                'ad_storage': 'denied'
            });
        }
    };

    const handleSavePreferences = () => {
        const consent = {
            ...preferences,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('cookie_consent', JSON.stringify(consent));
        setIsVisible(false);

        // Update analytics consent based on preferences
        if (window.gtag) {
            window.gtag('consent', 'update', {
                'analytics_storage': preferences.analytics ? 'granted' : 'denied',
                'ad_storage': preferences.marketing ? 'granted' : 'denied'
            });
        }
    };

    const content = {
        it: {
            title: "Questo sito utilizza i cookie",
            description: "Utilizziamo cookie tecnici per garantire il corretto funzionamento del sito e, previo tuo consenso, cookie analitici per migliorare la tua esperienza di navigazione.",
            acceptAll: "Accetta tutti",
            rejectAll: "Rifiuta",
            customize: "Personalizza",
            savePreferences: "Salva preferenze",
            backToSimple: "Indietro",
            necessary: "Cookie Necessari",
            necessaryDesc: "Questi cookie sono essenziali per il funzionamento del sito e non possono essere disattivati.",
            analytics: "Cookie Analitici",
            analyticsDesc: "Ci aiutano a capire come i visitatori utilizzano il sito, permettendoci di migliorarlo.",
            marketing: "Cookie di Marketing",
            marketingDesc: "Utilizzati per tracciare i visitatori e mostrare annunci pertinenti.",
            learnMore: "Per maggiori informazioni, consulta la nostra",
            cookiePolicy: "Cookie Policy",
            and: "e",
            privacyPolicy: "Privacy Policy"
        },
        en: {
            title: "This site uses cookies",
            description: "We use technical cookies to ensure the correct functioning of the site and, with your consent, analytical cookies to improve your browsing experience.",
            acceptAll: "Accept All",
            rejectAll: "Reject",
            customize: "Customize",
            savePreferences: "Save Preferences",
            backToSimple: "Back",
            necessary: "Necessary Cookies",
            necessaryDesc: "These cookies are essential for the site to function and cannot be disabled.",
            analytics: "Analytics Cookies",
            analyticsDesc: "Help us understand how visitors use the site, allowing us to improve it.",
            marketing: "Marketing Cookies",
            marketingDesc: "Used to track visitors and show relevant ads.",
            learnMore: "For more information, please see our",
            cookiePolicy: "Cookie Policy",
            and: "and",
            privacyPolicy: "Privacy Policy"
        }
    };

    const t = language === 'it' ? content.it : content.en;

    if (!isVisible) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 100, opacity: 0 }}
                className="fixed bottom-0 left-0 right-0 z-[300] p-4 md:p-6"
            >
                <div className="max-w-6xl mx-auto bg-gray-900 border border-[--accent]/30 rounded-2xl shadow-2xl overflow-hidden">
                    {!showCustomize ? (
                        // Simple View
                        <div className="p-6 md:p-8">
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 w-12 h-12 bg-[--accent]/20 rounded-full flex items-center justify-center">
                                    <Cookie className="w-6 h-6 text-[--accent]" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-bold text-white mb-2">{t.title}</h3>
                                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                                        {t.description}
                                    </p>
                                    <p className="text-gray-400 text-xs mb-6">
                                        {t.learnMore}{' '}
                                        <button
                                            onClick={onOpenCookiePolicy}
                                            className="text-[--accent] hover:underline font-medium"
                                        >
                                            {t.cookiePolicy}
                                        </button>
                                        {' '}{t.and}{' '}
                                        <button
                                            onClick={onOpenPrivacyPolicy}
                                            className="text-[--accent] hover:underline font-medium"
                                        >
                                            {t.privacyPolicy}
                                        </button>
                                        .
                                    </p>
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <button
                                            onClick={handleAcceptAll}
                                            className="px-6 py-3 bg-[--accent] hover:bg-[--accent-hover] text-black font-semibold rounded-lg transition-colors"
                                        >
                                            {t.acceptAll}
                                        </button>
                                        <button
                                            onClick={handleRejectAll}
                                            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors"
                                        >
                                            {t.rejectAll}
                                        </button>
                                        <button
                                            onClick={() => setShowCustomize(true)}
                                            className="px-6 py-3 border border-gray-600 hover:border-[--accent] text-gray-300 hover:text-[--accent] font-semibold rounded-lg transition-colors flex items-center gap-2 justify-center"
                                        >
                                            <Settings className="w-4 h-4" />
                                            {t.customize}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        // Customization View
                        <div className="p-6 md:p-8">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-xl font-bold text-white">{t.customize}</h3>
                                <button
                                    onClick={() => setShowCustomize(false)}
                                    className="text-gray-400 hover:text-white transition-colors"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-4 mb-6">
                                {/* Necessary Cookies */}
                                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-white font-semibold">{t.necessary}</label>
                                        <div className="relative inline-block w-12 h-6">
                                            <input
                                                type="checkbox"
                                                checked={true}
                                                disabled
                                                className="sr-only"
                                            />
                                            <div className="block bg-[--accent] w-12 h-6 rounded-full opacity-50 cursor-not-allowed"></div>
                                            <div className="dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform translate-x-6"></div>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">{t.necessaryDesc}</p>
                                </div>

                                {/* Analytics Cookies */}
                                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-white font-semibold">{t.analytics}</label>
                                        <div className="relative inline-block w-12 h-6">
                                            <input
                                                type="checkbox"
                                                checked={preferences.analytics}
                                                onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                                                className="sr-only"
                                            />
                                            <div
                                                onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                                                className={`block w-12 h-6 rounded-full cursor-pointer transition-colors ${preferences.analytics ? 'bg-[--accent]' : 'bg-gray-600'
                                                    }`}
                                            ></div>
                                            <div
                                                onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                                                className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform cursor-pointer ${preferences.analytics ? 'translate-x-6' : ''
                                                    }`}
                                            ></div>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">{t.analyticsDesc}</p>
                                </div>

                                {/* Marketing Cookies */}
                                <div className="bg-gray-800/50 p-4 rounded-lg border border-gray-700">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-white font-semibold">{t.marketing}</label>
                                        <div className="relative inline-block w-12 h-6">
                                            <input
                                                type="checkbox"
                                                checked={preferences.marketing}
                                                onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                                                className="sr-only"
                                            />
                                            <div
                                                onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                                                className={`block w-12 h-6 rounded-full cursor-pointer transition-colors ${preferences.marketing ? 'bg-[--accent]' : 'bg-gray-600'
                                                    }`}
                                            ></div>
                                            <div
                                                onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                                                className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition transform cursor-pointer ${preferences.marketing ? 'translate-x-6' : ''
                                                    }`}
                                            ></div>
                                        </div>
                                    </div>
                                    <p className="text-gray-400 text-sm">{t.marketingDesc}</p>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleSavePreferences}
                                    className="px-6 py-3 bg-[--accent] hover:bg-[--accent-hover] text-black font-semibold rounded-lg transition-colors"
                                >
                                    {t.savePreferences}
                                </button>
                                <button
                                    onClick={() => setShowCustomize(false)}
                                    className="px-6 py-3 border border-gray-600 hover:border-gray-500 text-gray-300 font-semibold rounded-lg transition-colors"
                                >
                                    {t.backToSimple}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
